import { z } from 'zod'

const nullableText = z.string().trim().max(10_000).nullable()
const nullableEmail = z
  .union([z.email().max(320), z.literal(''), z.null()])
  .transform((value) => (value ? value : null))

export const directoryImportContactSchema = z
  .object({
    name: z.string().trim().min(1).max(300),
    position: z.string().trim().min(1).max(300),
    phone: nullableText,
    email: nullableEmail,
    note: nullableText.default(null),
  })
  .strict()

const importMeta = {
  sourceIndex: z.number().int().nonnegative(),
  action: z.enum(['create', 'update', 'skip']).default('create'),
  duplicateId: z.string().trim().min(1).nullable().default(null),
  duplicateReason: z.enum(['edrpou', 'name']).nullable().default(null),
}

export const supplierImportRowSchema = z
  .object({
    ...importMeta,
    name: z.string().trim().min(1).max(500),
    edrpou: z
      .string()
      .trim()
      .regex(/^\d{8,10}$/)
      .nullable(),
    groups: z.array(z.string().trim().min(1).max(200)).max(50).default([]),
    city: z.string().trim().min(1).max(300).default('—'),
    address: nullableText.default(null),
    phone: nullableText.default(null),
    email: nullableEmail.default(null),
    contacts: z.array(directoryImportContactSchema).max(100).default([]),
    note: nullableText.default(null),
  })
  .strict()

export const customerImportRowSchema = z
  .object({
    ...importMeta,
    kind: z.enum(['company', 'fop', 'person']).default('company'),
    name: z.string().trim().min(1).max(300),
    edrpou: z
      .string()
      .trim()
      .regex(/^(\d{8}|\d{10})$/)
      .nullable(),
    city: z.string().trim().min(1).max(300).default('—'),
    address: nullableText.default(null),
    phone: nullableText.default(null),
    email: nullableEmail.default(null),
    contacts: z.array(directoryImportContactSchema).max(100).default([]),
    note: nullableText.default(null),
    // Names, not ids: the commit reuses a matching category or creates it.
    category: z.string().trim().min(1).max(200).nullable().default(null),
    subcategory: z.string().trim().min(1).max(200).nullable().default(null),
  })
  .strict()

export const supplierImportCommitSchema = z
  .object({ rows: z.array(supplierImportRowSchema).min(1).max(2_000) })
  .strict()
export const customerImportCommitSchema = z
  .object({ rows: z.array(customerImportRowSchema).min(1).max(2_000) })
  .strict()
export const directoryImportTextSchema = z
  .object({
    text: z.string().min(1).max(2_000_000),
    fileName: z.string().trim().max(500).default('вставлений текст.txt'),
    modelId: z.string().trim().min(1).max(200).optional(),
  })
  .strict()

export type DirectoryImportContact = z.infer<typeof directoryImportContactSchema>
export type SupplierImportRow = z.infer<typeof supplierImportRowSchema>
export type CustomerImportRow = z.infer<typeof customerImportRowSchema>

export interface DirectoryImportPreview<T> {
  rows: T[]
  method: 'table' | 'ai'
  warnings: string[]
}

export interface DirectoryImportSummary {
  created: number
  updated: number
  skipped: number
  contacts: number
  groups?: number
}
